import React, { useState } from "react";

import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../components.smart/DownloadButtonSSE";
import FilterChips from "../../../components/FilterChips";
import Pagination from "../../../components/Pagination";
import CompanyMeetingsFilterPanel from "./CompanyMeetingsFilterPanel";
import CompanyMeetingsTable from "./CompanyMeetingsTable";

import ErrorPage from "../../../pages/Error";

import { useMeetingsSearch } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useForm from "../../../hooks/useForm";
import { useUserAsyncTask } from "../../../hooks/useUserAsyncTask";

import { MeetingsSearchSortableFields, exportCompanyMeetingsCsv } from "../../../api/services/defs/internal-api";

import { convertFiltersToFilterChips, getMeetingsSearchServiceFilters } from "../utils";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { config } from "../config";
import { CompanyMeetingsFilters, CompanyMeetingsFiltersSchema } from "../schema";

const CompanyMeetings = () => {
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_COMPANY_MEETINGS_CSV
	);

	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<CompanyMeetingsFilters>({
		defaultSortName: config.defaultSortName,
		defaultSortDirection: config.defaultSortDirection,
		defaultPageNumber: config.defaultPageNumber,
	});

	// Filter form state for the filter panel slideout
	const {
		state: filterFormState,
		updateForm: updateFilterForm,
		setInitialState: setInitialFilterForm,
	} = useForm({
		schema: CompanyMeetingsFiltersSchema,
		defaultValues: filters,
		disableWarning: true,
	});

	// Fetch company meetings for admin
	const {
		data: meetings = [],
		isLoading: isFetchingMeetings,
		totalCount: totalMeetingsCount = 0,
	} = useMeetingsSearch<"companyMeetings">({
		...getMeetingsSearchServiceFilters(filters, debouncedSearchValue),
		limit: config.pageSize,
		skip: (pageNumber - 1) * config.pageSize,
		sort: { name: sort.name as MeetingsSearchSortableFields, direction: sort.direction },
	});

	// Wait for feature flag to load, then show access denied if disabled
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Handle column header click for sorting
	const handleColumnHeaderClick = (column: { name: string; direction: "asc" | "desc" }) => {
		setSort(column);
	};

	// Handle CSV download
	const handleDownloadCsv = async () => {
		await triggerExport(() =>
			exportCompanyMeetingsCsv({
				filters: getMeetingsSearchServiceFilters(filters, debouncedSearchValue),
				sort: { name: sort.name as MeetingsSearchSortableFields, direction: sort.direction },
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			})
		);
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search Meetings..."
			onFilterButtonPress={() => setIsFilterPanelOpen(true)}
		/>
	);

	const toolbarInformationRow = (
		<FilterChips
			filters={convertFiltersToFilterChips(filters)}
			label={convertFiltersToFilterChips(filters).length > 0 ? "Filters applied: " : ""}
			onDeleteFilter={(id) => {
				const currentFilters = { ...filters };
				if (id === "status") {
					delete currentFilters.status;
				} else if (id === "dateRange") {
					delete currentFilters.dateRangeStart;
					delete currentFilters.dateRangeEnd;
				} else {
					delete currentFilters[id];
				}
				setFilters({ ...currentFilters });
				setInitialFilterForm({ ...currentFilters }, true);
			}}
		/>
	);

	const filterPanel = (
		<CompanyMeetingsFilterPanel
			isOpen={isFilterPanelOpen}
			onClose={() => setIsFilterPanelOpen(false)}
			onApplyFilters={(filters) => {
				// Preserve the current sort when applying filters
				const currentSort = sort;
				setFilters(filters);
				setInitialFilterForm(filters, true);
				// Restore the sort that was active before the filter was applied
				setSort(currentSort);
			}}
			filters={filterFormState}
			updateForm={updateFilterForm}
		/>
	);

	const mainContent = (
		<div className="space-y-6">
			<CompanyMeetingsTable
				meetings={meetings}
				isFetchingMeetings={isFetchingMeetings}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				handleColumnHeaderClick={handleColumnHeaderClick}
			/>
			<Pagination
				currentPage={pageNumber}
				pageCount={Math.ceil(totalMeetingsCount / config.pageSize)}
				onClick={(page: number) => setPageNumber(page)}
			/>
		</div>
	);

	const actionButtons = [
		<DownloadButtonSSE task={exportTask} onClick={handleDownloadCsv} key="download-csv" testid="download-csv">
			Download CSV
		</DownloadButtonSSE>,
	];

	return (
		<InfoPageTemplate
			pageTitle="Meetings"
			mainContent={mainContent}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			navigationLinks={[{ key: "all-meetings", label: "All Meetings", href: "/company/meetings" }]}
			activeNavigationLinkKey="all-meetings"
			drawer={filterPanel}
		/>
	);
};

export default CompanyMeetings;
